import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs';
import { CONST_URL_SEGEMENTS } from 'paylink-constants';
import { ApiService } from './api.service';
import { LeadCaptureWidgetSubmission } from '../interfaces/lead-capture-submission.interface';
import { normalizeMobileCountryCode } from './tour-booking-submission.util';
import { WidgetRecaptchaSubmitPayload } from './recaptcha/widget-recaptcha.types';
import { buildWidgetApiPath } from '../utils/widget-api-path.util';

export interface FormSubmissionResponse {
  id: string;
  success?: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class FormSubmissionService {
  private readonly http = inject(HttpClient);
  private readonly apiService = inject(ApiService);

  submitForm(
    widgetId: string,
    formData: LeadCaptureWidgetSubmission & {
      widget_id?: string;
      notes?: string;
      data?: LeadCaptureWidgetSubmission['data'];
    },
    tourDateTime?: string,
    captcha?: WidgetRecaptchaSubmitPayload,
  ): Observable<FormSubmissionResponse> {
    let payload: Record<string, unknown>;

    if (tourDateTime) {
      payload = {
        widget_type: 'TourBooking',
        widget_id: widgetId,
        data: this.processFormData(formData as unknown as Record<string, unknown>),
        book_tour: {
          notes: formData.notes || '',
          scheduled_datetime: tourDateTime,
        },
        trial_membership: '',
      };
    } else {
      payload = {
        widget_type: formData.widget_type || 'LeadCapture',
        data: this.processFormData(formData.data ?? formData),
        book_tour: '',
        trial_membership: '',
      };
    }

    return this.http.post<FormSubmissionResponse>(
      this.postWidgetDataUrl(widgetId),
      payload,
      this.buildRequestOptions(captcha),
    );
  }

  /** Tour booking widget: LeadCapture payload with book_tour datetime + notes. */
  submitTourBookingLeadCapture(
    widgetId: string,
    submission: LeadCaptureWidgetSubmission,
    captcha?: WidgetRecaptchaSubmitPayload,
  ): Observable<FormSubmissionResponse> {
    const bookTour = submission.book_tour;
    if (
      bookTour === '' ||
      typeof bookTour !== 'object' ||
      bookTour === null
    ) {
      throw new Error('Tour booking requires book_tour details');
    }

    const scheduled_datetime = bookTour.scheduled_datetime;
    const notes = bookTour.notes ?? '';

    if (!scheduled_datetime) {
      throw new Error('Tour booking requires book_tour.scheduled_datetime');
    }

    const payload = {
      widget_type: submission.widget_type,
      data: this.processFormData(submission.data),
      book_tour: {
        scheduled_datetime,
        notes,
      },
      trial_membership: '',
    };

    return this.http.post<FormSubmissionResponse>(
      this.postWidgetDataUrl(widgetId),
      payload,
      this.buildRequestOptions(captcha),
    );
  }

  /** Trial booking widget: LeadCapture payload with trial_membership date + notes. */
  submitTrialBookingLeadCapture(
    widgetId: string,
    submission: LeadCaptureWidgetSubmission,
    captcha?: WidgetRecaptchaSubmitPayload,
  ): Observable<FormSubmissionResponse> {
    if (submission.widget_type !== 'TrialBooking') {
      throw new Error('Trial booking requires widget_type TrialBooking');
    }

    const trialMembership = submission.trial_membership;
    if (
      trialMembership === '' ||
      typeof trialMembership !== 'object' ||
      trialMembership === null
    ) {
      throw new Error('Trial booking requires trial_membership details');
    }

    const trial_start_date = trialMembership.trial_start_date;
    const notes = trialMembership.notes ?? '';

    if (!trial_start_date) {
      throw new Error('Trial booking requires trial_membership.trial_start_date');
    }

    const payload = {
      widget_type: submission.widget_type,
      data: this.processFormData(submission.data),
      book_tour: '',
      trial_membership: {
        trial_start_date,
        notes,
      },
    };

    return this.http.post<FormSubmissionResponse>(
      this.postWidgetDataUrl(widgetId),
      payload,
      this.buildRequestOptions(captcha),
    );
  }

  private postWidgetDataUrl(widgetId: string): string {
    const path = buildWidgetApiPath(
      CONST_URL_SEGEMENTS.OPEN_API.WIDGETS.POST_WIDGET_DATA,
      { widget_id: widgetId },
    );
    return `${this.apiService.getBaseApiUrl()}${path}`;
  }

  private buildRequestOptions(captcha?: WidgetRecaptchaSubmitPayload): {
    headers?: HttpHeaders;
  } {
    if (!captcha?.token) {
      return {};
    }

    let headers = new HttpHeaders()
      .set('X-Recaptcha-Token', captcha.token)
      .set('X-Recaptcha-Version', captcha.version);

    if (captcha.action) {
      headers = headers.set('X-Recaptcha-Action', captcha.action);
    }

    return { headers };
  }

  private processFormData(formData: Record<string, unknown>): Record<string, unknown> {
    const processedData: Record<string, unknown> = {
      tenant_uuid: formData['tenant_uuid'] ?? '',
      full_name: formData['full_name'] || formData['name'],
      email_id: formData['email_id'] || formData['email'],
      mobile_country_code: normalizeMobileCountryCode(
        formData['mobile_country_code'],
      ),
      mobile_number: formData['mobile_number']
        ? parseInt(String(formData['mobile_number']), 10)
        : undefined,
      date_of_birth: formData['date_of_birth'],
      gender: formData['gender']
        ? String(formData['gender']).toLowerCase()
        : 'unknown',
      location: formData['location'],
      source: formData['source'],
      channel: formData['channel'],
      medical_alert: formData['medical_alert'],
      note: formData['note'],
      client_external_cms_id: formData['client_external_cms_id'],
      migration_id: formData['migration_id'],
      national_id: formData['national_id'] || formData['aadhar'],
      fitness_goals: formData['fitness_goals'],
      previous_gym_experience: formData['previous_gym_experience'],
      client_communication_concent:
        formData['client_communication_concent'] || formData['terms_accepted'],
      service_interest: formData['service_interest'],
      age: formData['age'],
      dob: formData['dob'],
      clt_source: formData['clt_source'],
      clt_channel: formData['clt_channel'],
      clt_rep: formData['clt_rep'],
    };

    Object.keys(processedData).forEach((key) => {
      if (key !== 'tenant_uuid' && processedData[key] === undefined) {
        delete processedData[key];
      }
    });

    return processedData;
  }
}
